import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getStory } from '../../api/stories';
import Button from '../../components/common/Button';
import PracticeChoiceModal from '../../components/story/PracticeChoiceModal';
import ReviewReel from '../../components/story/ReviewReel';
import ReviewSummary from '../../components/story/ReviewSummary';
import useFocusOnMount from '../../hooks/useFocusOnMount';
import audioManager from '../../lib/audioManager';
import s from './StoryReviewPage.module.css';

export default function StoryReviewPage() {
  const { t } = useTranslation();
  const mainRef = useFocusOnMount();
  const { storyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [story, setStory] = useState(location.state?.story || null);
  const [loading, setLoading] = useState(!story?.characters);
  const [error, setError] = useState(null);
  const [view, setView] = useState('reel');
  const [hardCharIds, setHardCharIds] = useState([]);
  const [showPracticeModal, setShowPracticeModal] = useState(false);

  // Review-reel BGM (audio-system.md). A dedicated upbeat track for the reel;
  // fades out on unmount so the map biome track crossfades back in on return.
  useEffect(() => {
    audioManager.playBGM('review-reel', 'review');
    return () => audioManager.stopBGM();
  }, []);

  useEffect(() => {
    if (story?.characters) return;
    if (!storyId) {
      navigate('/bookshelf', { replace: true });
      return;
    }
    getStory(storyId)
      .then((data) => setStory(data))
      .catch((err) => setError(err.message || 'Failed to load story.'))
      .finally(() => setLoading(false));
  }, [storyId, story, navigate]);

  // Guard: if already reviewed, redirect to bookshelf.
  useEffect(() => {
    if (story && story.status === 'reviewed') {
      navigate('/bookshelf', { replace: true });
    }
  }, [story, navigate]);

  function handleReelComplete(ids) {
    setHardCharIds(ids || []);
    setView('summary');
  }

  function handlePractice() {
    setShowPracticeModal(true);
  }

  function handleHome() {
    navigate('/', { replace: true });
  }

  if (loading) {
    return (
      <div className={s.loadingState} aria-live="polite">
        <p className={s.loadingText}>{t('review.loadingReview')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={s.errorState}>
        <p className={s.errorHeading}>{error}</p>
        <Button onClick={() => navigate('/bookshelf', { replace: true })}>
          Back to Bookshelf
        </Button>
      </div>
    );
  }

  return (
    <main className={s.page} ref={mainRef} tabIndex={-1}>
      <h1 className={s.heading}>{t('review.letsReview')}</h1>
      <p className={`${s.subtitle} ${s.subtitleMobile}`}>
        {t('review.swipeInstruction')}
      </p>
      <p className={`${s.subtitle} ${s.subtitleDesktop}`}>
        {t('review.swipeInstructionDesktop')}
      </p>

      <div className={s.reelContainer}>
        {view === 'reel' ? (
          <>
            <ReviewReel
              characters={story?.characters || []}
              onComplete={handleReelComplete}
            />
            <button
              type="button"
              className={s.reviewLaterBtn}
              onClick={handleHome}
            >
              {t('review.reviewLater')}
            </button>
          </>
        ) : (
          <ReviewSummary
            story={story}
            hardCharIds={hardCharIds}
            onPractice={handlePractice}
            onHome={handleHome}
          />
        )}
      </div>

      <PracticeChoiceModal
        isOpen={showPracticeModal}
        onClose={() => setShowPracticeModal(false)}
        story={story}
      />
    </main>
  );
}
